import {
  dokumentKategorieLabel,
  fristAmpel,
  istPinForm,
  personenAmpel,
  standJePerson,
  teilnahmeName,
} from '../../../services/sifa/kundenportalLogic';
import { heuteIso } from '../../../services/sifa/teilnahmeLogic';
import type { KundenportalDaten, PortalDokument, PortalMitarbeiter, PortalTeilnahme } from '../../../services/sifa/kundenportalTypes';

/**
 * Die Regeln der Kundenseite — rein, ohne DOM: Reiter, PIN-Sitzung,
 * Kennzahlen der Uebersicht, Filter und Gruppierungen der Listen.
 * Die fachlichen Regeln (Ampeln, Mapper, PIN-Form) liegen in
 * services/sifa/kundenportalLogic.ts; hier steht nur, was die Bedienung angeht.
 */

export type PortalTab = 'uebersicht' | 'mitarbeiter' | 'unterweisungen' | 'dokumente' | 'fristen';

export const TABS: ReadonlyArray<{ id: PortalTab; label: string }> = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'mitarbeiter', label: 'Mitarbeiter' },
  { id: 'unterweisungen', label: 'Unterweisungen' },
  { id: 'dokumente', label: 'Dokumente' },
  { id: 'fristen', label: 'Fristen' },
];

export const istTab = (wert: unknown): wert is PortalTab => TABS.some((t) => t.id === wert);

/** Schluessel im sessionStorage: die PIN je Link, nur fuer diesen Tab. */
export const sessionKey = (token: string): string => `kundenportal:${(token || '').trim()}`;

/** Gespeicherte PIN, wenn sie die Form hat; sonst null (nichts Halbes uebernehmen). */
export const lesePin = (roh: string | null | undefined): string | null => {
  const s = (roh || '').trim();
  return istPinForm(s) ? s : null;
};

export interface UebersichtZaehler {
  mitarbeiterAktiv: number;
  mitarbeiterInaktiv: number;
  /** Aktive Personen mit gueltigem Nachweis (juengste bestandene Teilnahme nicht abgelaufen). */
  nachweiseGueltig: number;
  nachweiseBald: number;
  nachweiseAbgelaufen: number;
  ohneNachweis: number;
  kurseAktiv: number;
  fristenOffen: number;
  fristenUeberfaellig: number;
  fristenBald: number;
  dokumente: number;
}

/** Kennzahlen der Uebersicht in einem Durchlauf; nur AKTIVE Personen zaehlen bei den Nachweisen. */
export const zaehlerUebersicht = (daten: KundenportalDaten, heute: string = heuteIso()): UebersichtZaehler => {
  const stand = standJePerson(daten.teilnahmen);
  const z: UebersichtZaehler = {
    mitarbeiterAktiv: 0,
    mitarbeiterInaktiv: 0,
    nachweiseGueltig: 0,
    nachweiseBald: 0,
    nachweiseAbgelaufen: 0,
    ohneNachweis: 0,
    kurseAktiv: daten.unterweisungen.filter((u) => u.status === 'aktiv').length,
    fristenOffen: daten.fristen.length,
    fristenUeberfaellig: 0,
    fristenBald: 0,
    dokumente: daten.dokumente.length,
  };
  for (const m of daten.mitarbeiter) {
    if (!m.aktiv) {
      z.mitarbeiterInaktiv += 1;
      continue;
    }
    z.mitarbeiterAktiv += 1;
    const s = personenAmpel(stand[m.id] || { letzte: null, gueltigBis: null, historie: [] }, heute);
    if (s === 'gueltig') z.nachweiseGueltig += 1;
    else if (s === 'bald') z.nachweiseBald += 1;
    else if (s === 'abgelaufen') z.nachweiseAbgelaufen += 1;
    else z.ohneNachweis += 1;
  }
  for (const f of daten.fristen) {
    const a = fristAmpel(f.faelligAm, heute);
    if (a === 'ueberfaellig') z.fristenUeberfaellig += 1;
    else if (a === 'bald') z.fristenBald += 1;
  }
  return z;
};

/** Titel eines Kurses zur id — „Unterweisung" als Ersatz, wenn er nicht (mehr) in der Liste ist. */
export const unterweisungTitel = (daten: Pick<KundenportalDaten, 'unterweisungen'>, id: string): string => {
  const u = daten.unterweisungen.find((x) => x.id === id);
  return u ? (u.titel || `Unterweisung Nr. ${u.nummer}`) : 'Unterweisung';
};

/** Teilnahmen je Kurs-id (fuer die Zaehler auf den Kurskarten). */
export const teilnahmenJeUnterweisung = (teilnahmen: PortalTeilnahme[]): Record<string, { gesamt: number; bestanden: number }> => {
  const out: Record<string, { gesamt: number; bestanden: number }> = {};
  for (const t of teilnahmen) {
    const z = (out[t.unterweisungId] ||= { gesamt: 0, bestanden: 0 });
    z.gesamt += 1;
    if (t.bestanden) z.bestanden += 1;
  }
  return out;
};

export interface TeilnahmenFilter {
  /** '' = alle Kurse. */
  unterweisungId: string;
  suche: string;
}

/** Filter nach Kurs und Namensteil; Reihenfolge bleibt (juengste zuerst). */
export const filtereTeilnahmen = (
  teilnahmen: PortalTeilnahme[],
  filter: TeilnahmenFilter,
  mitarbeiter: PortalMitarbeiter[],
): PortalTeilnahme[] => {
  const s = (filter.suche || '').trim().toLowerCase();
  return teilnahmen.filter((t) => {
    if (filter.unterweisungId && t.unterweisungId !== filter.unterweisungId) return false;
    if (!s) return true;
    return teilnahmeName(t, mitarbeiter).toLowerCase().includes(s);
  });
};

/** Dokumente je Kategorie, Kategorien alphabetisch nach Label, innerhalb juengste zuerst. */
export const gruppiereDokumente = (dokumente: PortalDokument[]): Array<{ kategorie: string; label: string; dokumente: PortalDokument[] }> => {
  const gruppen = new Map<string, PortalDokument[]>();
  for (const d of dokumente) {
    const k = d.kategorie || 'sonstiges';
    (gruppen.get(k) || gruppen.set(k, []).get(k)!).push(d);
  }
  return [...gruppen.entries()]
    .map(([kategorie, liste]) => ({
      kategorie,
      label: dokumentKategorieLabel(kategorie),
      dokumente: [...liste].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'de'));
};
